import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import { useMode } from '../contexts/ModeContext';
import { useLang } from '../contexts/LangContext';
import { authAPI, hafizlikAPI, quranAPI, speechAPI } from '../utils/api';
import { playPageFlipSound } from '../utils/pageFlipSound';
import {
  ChevronLeft, ChevronRight, ArrowLeft, Mic, MicOff, Play, Pause,
  CheckCircle2, RotateCw, BookOpen, Volume2,
} from 'lucide-react';
import { toast } from 'sonner';

const TOTAL_PAGES = 604;

const HafizlikReader = () => {
  const { pageNumber } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const { isKidsMode } = useMode();
  const { lang } = useLang();

  const initialPage = Math.min(TOTAL_PAGES, Math.max(1, parseInt(pageNumber || '1', 10)));
  const initialMode = search.get('mode') === 'reviewing' ? 'reviewing' : 'learning';

  const [user, setUser] = useState(null);
  const [page, setPage] = useState(initialPage);
  const [hifzMode, setHifzMode] = useState(initialMode);
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [flipDir, setFlipDir] = useState(null); // 'forward' | 'backward' | null
  const [pageStatus, setPageStatus] = useState({}); // {page: 'in_progress'|'memorized'}

  // Audio (full surah / per-ayah)
  const [reciter, setReciter] = useState(() => {
    try { return localStorage.getItem('hafizim_reciter') || 'alafasy'; } catch { return 'alafasy'; }
  });
  useEffect(() => { try { localStorage.setItem('hafizim_reciter', reciter); } catch (_) { /* ignore */ } }, [reciter]);
  const [playingAyah, setPlayingAyah] = useState(null); // "surah-ayah" key
  const audioRef = useRef(null);

  // Reviewing mode mic
  const [recording, setRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [matchResult, setMatchResult] = useState(null); // {score, is_correct}
  const [autoAdvance, setAutoAdvance] = useState(() => {
    try { return localStorage.getItem('hafizim_auto_advance') !== 'false'; } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem('hafizim_auto_advance', String(autoAdvance)); } catch (_) { /* ignore */ }
  }, [autoAdvance]);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const tone = isKidsMode
    ? { primaryBg: 'bg-amber-500 hover:bg-amber-600', softBg: 'bg-amber-50', softText: 'text-amber-800', border: 'border-amber-200', accent: 'text-amber-600' }
    : { primaryBg: 'bg-teal-700 hover:bg-teal-800', softBg: 'bg-teal-50', softText: 'text-teal-800', border: 'border-teal-200', accent: 'text-teal-600' };

  // Load user + initial page progress map (for next/prev styling)
  useEffect(() => {
    (async () => {
      try {
        const [u, pp] = await Promise.all([authAPI.getMe(), hafizlikAPI.listPageProgress()]);
        setUser(u.data);
        const map = {};
        for (const p of (pp.data?.pages || [])) map[p.page] = p.status;
        setPageStatus(map);
      } catch (_) { /* ignore */ }
    })();
  }, []);

  // Load page content whenever `page` changes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await quranAPI.getPage(page, 'quran-uthmani', true, 'tr.diyanet');
        if (!cancelled) setPageData(r.data);
        // Save last position (silently)
        hafizlikAPI.saveLastPosition(page, hifzMode).catch(() => {});
      } catch (e) {
        if (!cancelled) toast.error(lang === 'tr' ? 'Sayfa yüklenemedi' : 'Page failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Cleanup audio + mic on unmount
  useEffect(() => () => {
    try { audioRef.current?.pause(); } catch (_) { /* ignore */ }
    try { mediaRecorderRef.current?.stream?.getTracks().forEach((t) => t.stop()); } catch (_) { /* ignore */ }
  }, []);

  const goToPage = (target, dir) => {
    if (target < 1 || target > TOTAL_PAGES || target === page) return;
    try { playPageFlipSound(dir); } catch (_) { /* ignore */ }
    setFlipDir(dir);
    setMatchResult(null);
    setTranscript('');
    setTimeout(() => {
      setPage(target);
      // Keep URL in sync so refresh/share works
      navigate(`/hafizlik/sayfa/${target}?mode=${hifzMode}`, { replace: true });
      setTimeout(() => setFlipDir(null), 420);
    }, 80);
  };

  const handlePrev = () => goToPage(page - 1, 'backward');
  const handleNext = () => goToPage(page + 1, 'forward');

  const togglePlayAyah = async (surahNum, ayahNum) => {
    const key = `${surahNum}-${ayahNum}`;
    if (playingAyah === key && audioRef.current) {
      try { audioRef.current.pause(); } catch (_) { /* ignore */ }
      setPlayingAyah(null);
      return;
    }
    try { audioRef.current?.pause(); } catch (_) { /* ignore */ }
    const url = quranAPI.getAyahAudioUrl(reciter, surahNum, ayahNum);
    const a = new Audio(url);
    audioRef.current = a;
    a.onended = () => setPlayingAyah(null);
    a.onerror = () => { setPlayingAyah(null); toast.error(lang === 'tr' ? 'Ses oynatılamadı' : 'Playback failed'); };
    try { await a.play(); setPlayingAyah(key); } catch (_) { setPlayingAyah(null); }
  };

  const markPage = async (status) => {
    try {
      await hafizlikAPI.upsertPageProgress(page, status, hifzMode);
      setPageStatus((m) => ({ ...m, [page]: status }));
      toast.success(
        status === 'memorized'
          ? (lang === 'tr' ? 'Sayfa ezberlendi olarak işaretlendi!' : 'Marked as memorized!')
          : (lang === 'tr' ? 'Sayfa devam ediyor olarak işaretlendi' : 'Marked in progress')
      );
    } catch {
      toast.error(lang === 'tr' ? 'Kaydedilemedi' : 'Save failed');
    }
  };

  // Page text (all ayahs concatenated) - used for transcription compare
  const fullArabicText = useMemo(() => {
    if (!pageData?.groups) return '';
    return pageData.groups.flatMap((g) => g.ayahs.map((a) => a.text)).join(' ');
  }, [pageData]);

  // ----- Mic recording for Hafız mode -----
  const startRecording = async () => {
    if (recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        try {
          const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          const fd = new FormData();
          fd.append('audio', blob, 'audio.webm');
          // We compare against the page's first ayah, which is what user is reciting
          // For multi-ayah page, the server only checks similarity to expected, so we send full page text.
          // Backend signature: audio + surah_number + ayah_number. We use group[0]'s surah/ayah-from-this-page as anchor.
          const firstGroup = pageData?.groups?.[0];
          const firstAyah = firstGroup?.ayahs?.[0];
          if (!firstGroup || !firstAyah) {
            toast.error(lang === 'tr' ? 'Sayfa verisi bulunamadı' : 'Page data missing');
            return;
          }
          fd.append('surah_number', String(firstGroup.surah_number));
          fd.append('ayah_number', String(firstAyah.number_in_surah));
          const res = await speechAPI.transcribe(fd);
          const data = res.data || {};
          const score = data.similarity_score ?? data.score ?? 0;
          const isCorrect = !!data.is_correct;
          setTranscript(data.transcription || '');
          setMatchResult({ score, is_correct: isCorrect, errors: data.errors || [] });
          // Auto-advance to next page on a confident match (Hafız mode only)
          if (autoAdvance && isCorrect && score >= 0.85 && page < TOTAL_PAGES) {
            toast.success(
              lang === 'tr'
                ? `Doğru! Bir sonraki sayfaya geçiliyor… (%${(score * 100).toFixed(0)})`
                : `Correct! Advancing to next page… (${(score * 100).toFixed(0)}%)`
            );
            setTimeout(() => {
              // Mark this page as memorized then advance
              hafizlikAPI.upsertPageProgress(page, 'memorized', 'reviewing').catch(() => {});
              setPageStatus((m) => ({ ...m, [page]: 'memorized' }));
              goToPage(page + 1, 'forward');
            }, 1400);
          }
        } catch (e) {
          toast.error(lang === 'tr' ? 'Ses işlenemedi' : 'STT failed');
        } finally {
          try { stream.getTracks().forEach((t) => t.stop()); } catch (_) { /* ignore */ }
        }
      };
      mr.start();
      setRecording(true);
    } catch (e) {
      toast.error(lang === 'tr' ? 'Mikrofon izni reddedildi' : 'Mic permission denied');
    }
  };

  const stopRecording = () => {
    if (!recording) return;
    try { mediaRecorderRef.current?.stop(); } catch (_) { /* ignore */ }
    setRecording(false);
  };

  const currentStatus = pageStatus[page];
  const ayahFontSize = 30;

  return (
    <AppShell user={user} title={`${lang === 'tr' ? 'Sayfa' : 'Page'} ${page}`}>
      <div className="max-w-4xl mx-auto p-3 sm:p-6 md:p-10 space-y-5">
        {/* Top toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate('/hafizlik')}
            data-testid="hifz-back"
            className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-stone-900"
          >
            <ArrowLeft className="w-4 h-4" /> {lang === 'tr' ? 'Hafızlık' : 'Hifz'}
          </button>

          <div className="flex items-center gap-2 bg-white rounded-xl border border-stone-200 p-1">
            {[
              { id: 'learning', label: lang === 'tr' ? 'Hafızlık Yapan' : 'Memorizing' },
              { id: 'reviewing', label: lang === 'tr' ? 'Hafız' : 'Hafiz' },
            ].map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setHifzMode(m.id)}
                data-testid={`reader-mode-${m.id}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  hifzMode === m.id ? `${tone.primaryBg} text-white` : 'text-stone-600 hover:bg-stone-100'
                }`}
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >{m.label}</button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => markPage('in_progress')}
              data-testid="mark-in-progress"
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${currentStatus === 'in_progress' ? 'bg-yellow-100 border-yellow-300 text-yellow-900' : 'bg-white border-stone-200 text-stone-700 hover:border-stone-300'}`}
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              <RotateCw className="w-3 h-3 inline mr-1" /> {lang === 'tr' ? 'Devam ediyor' : 'In progress'}
            </button>
            <button
              type="button"
              onClick={() => markPage('memorized')}
              data-testid="mark-memorized"
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${currentStatus === 'memorized' ? `${tone.softBg} ${tone.border} ${tone.softText}` : 'bg-white border-stone-200 text-stone-700 hover:border-stone-300'}`}
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              <CheckCircle2 className="w-3 h-3 inline mr-1" /> {lang === 'tr' ? 'Ezberledim' : 'Memorized'}
            </button>
          </div>
        </div>

        {/* Page card with flip animation */}
        <div className="relative">
          <div
            data-testid="hifz-page-card"
            className={`bg-[#fdf8ee] rounded-2xl border-2 ${tone.border} shadow-lg p-6 sm:p-10 min-h-[60vh] overflow-hidden`}
            style={{
              perspective: '1500px',
              transformStyle: 'preserve-3d',
            }}
          >
            <div
              key={page}
              className={`origin-${flipDir === 'backward' ? 'right' : 'left'} transition-transform duration-500 ease-out`}
              style={{
                animation: flipDir
                  ? `${flipDir === 'forward' ? 'pageFlipFwd' : 'pageFlipBwd'} 0.42s ease-out`
                  : undefined,
              }}
            >
              {/* Page header */}
              <div className="flex items-center justify-between mb-4 pb-2 border-b border-stone-200">
                <div className="text-xs uppercase tracking-wider text-stone-500" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {pageData?.groups?.[0]?.surah_name_en || '—'}
                </div>
                <div className="text-sm font-bold text-stone-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {lang === 'tr' ? 'Sayfa' : 'Page'} {page}
                </div>
              </div>

              {loading ? (
                <div className="text-center py-20 text-stone-400">…</div>
              ) : !pageData?.groups?.length ? (
                <div className="text-center py-20 text-stone-400">{lang === 'tr' ? 'İçerik bulunamadı' : 'No content'}</div>
              ) : (
                <div className="space-y-6" style={{ direction: 'rtl', fontFamily: 'Amiri, serif' }}>
                  {pageData.groups.map((g, gi) => (
                    <div key={gi}>
                      {g.surah_number && (
                        <div className="text-center my-4">
                          <div className={`inline-block px-4 py-2 rounded-xl ${tone.softBg} ${tone.softText} font-bold`} style={{ fontFamily: 'Amiri, serif' }}>
                            {g.surah_name_ar}
                          </div>
                        </div>
                      )}
                      <p className="leading-loose text-stone-900 text-right" style={{ fontSize: ayahFontSize, lineHeight: 2.2 }}>
                        {g.ayahs.map((a, ai) => {
                          const key = `${g.surah_number}-${a.number_in_surah}`;
                          const isPlaying = playingAyah === key;
                          return (
                            <span key={ai} className="cursor-pointer hover:bg-amber-50/60 rounded px-1 transition-colors">
                              <span onClick={() => togglePlayAyah(g.surah_number, a.number_in_surah)} title={isPlaying ? 'Pause' : 'Play'}>
                                {a.text}
                              </span>
                              <span
                                className={`inline-flex items-center justify-center mx-1 w-7 h-7 rounded-full text-xs font-bold border ${isPlaying ? `${tone.primaryBg} text-white border-transparent` : 'bg-white text-stone-700 border-stone-300'}`}
                                onClick={() => togglePlayAyah(g.surah_number, a.number_in_surah)}
                              >
                                {a.number_in_surah}
                              </span>
                            </span>
                          );
                        })}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Nav arrows (overlay) */}
          <button
            type="button"
            onClick={handlePrev}
            disabled={page <= 1}
            data-testid="page-prev"
            className={`absolute -left-3 sm:-left-6 top-1/2 -translate-y-1/2 w-10 h-14 sm:w-12 sm:h-20 rounded-xl bg-white shadow-md border ${tone.border} flex items-center justify-center text-stone-700 hover:scale-105 disabled:opacity-30 transition-all`}
            aria-label="Previous page"
          >
            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
          <button
            type="button"
            onClick={handleNext}
            disabled={page >= TOTAL_PAGES}
            data-testid="page-next"
            className={`absolute -right-3 sm:-right-6 top-1/2 -translate-y-1/2 w-10 h-14 sm:w-12 sm:h-20 rounded-xl bg-white shadow-md border ${tone.border} flex items-center justify-center text-stone-700 hover:scale-105 disabled:opacity-30 transition-all`}
            aria-label="Next page"
          >
            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        {/* Footer: reciter + mic (only in reviewing mode) */}
        <div className="bg-white rounded-2xl border border-stone-200 p-4 space-y-3">
          {/* Reciter selector */}
          <div className="flex flex-wrap items-center gap-2">
            <Volume2 className="w-4 h-4 text-stone-500" />
            <span className="text-xs text-stone-500 mr-2">{lang === 'tr' ? 'Kari:' : 'Reciter:'}</span>
            {[
              { id: 'alafasy', label: 'Alafasy' },
              { id: 'abdulbasit', label: 'Abdul Basit' },
              { id: 'sudais', label: 'Sudais' },
              { id: 'husary', label: 'Husary' },
            ].map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => setReciter(r.id)}
                data-testid={`reader-reciter-${r.id}`}
                className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${
                  reciter === r.id ? `${tone.primaryBg} text-white border-transparent` : 'bg-white text-stone-700 border-stone-200 hover:border-stone-300'
                }`}
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >{r.label}</button>
            ))}
            <span className="text-[11px] text-stone-400 ml-2">{lang === 'tr' ? 'Ayet üstüne tıklayarak çal' : 'Tap any ayah to play'}</span>
          </div>

          {hifzMode === 'reviewing' && (
            <div className="border-t border-stone-200 pt-3 space-y-2">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="text-sm font-semibold text-stone-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <BookOpen className="inline w-4 h-4 mr-1" />
                  {lang === 'tr' ? 'Mikrofonla ezber ver' : 'Recite with mic'}
                </p>
                <label className="inline-flex items-center gap-2 text-xs text-stone-600 cursor-pointer select-none" data-testid="auto-advance-toggle">
                  <input
                    type="checkbox"
                    checked={autoAdvance}
                    onChange={(e) => setAutoAdvance(e.target.checked)}
                    className="rounded"
                  />
                  {lang === 'tr' ? 'Doğruysa otomatik ilerle' : 'Auto-advance on correct'}
                </label>
                <button
                  type="button"
                  onClick={recording ? stopRecording : startRecording}
                  data-testid="mic-toggle"
                  className={`px-4 py-2 rounded-xl text-white text-sm font-semibold inline-flex items-center gap-2 ${recording ? 'bg-red-600 hover:bg-red-700' : tone.primaryBg}`}
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {recording ? <><MicOff className="w-4 h-4" /> {lang === 'tr' ? 'Durdur' : 'Stop'}</> : <><Mic className="w-4 h-4" /> {lang === 'tr' ? 'Kaydet' : 'Record'}</>}
                </button>
              </div>
              {transcript && (
                <div className="bg-stone-50 rounded-xl p-3 space-y-1">
                  <p className="text-[10px] uppercase tracking-wider text-stone-500">{lang === 'tr' ? 'Tanınan' : 'Recognized'}</p>
                  <p className="text-base text-stone-800 text-right" dir="rtl" style={{ fontFamily: 'Amiri, serif' }}>{transcript}</p>
                  {matchResult && (
                    <p className={`text-xs font-semibold ${matchResult.is_correct ? 'text-emerald-700' : 'text-red-700'}`}>
                      {lang === 'tr' ? 'Benzerlik' : 'Similarity'}: {(matchResult.score * 100).toFixed(1)}% — {matchResult.is_correct ? (lang === 'tr' ? 'Doğru' : 'Correct') : (lang === 'tr' ? 'Tekrar dene' : 'Try again')}
                    </p>
                  )}
                </div>
              )}
              <p className="text-[11px] text-stone-400">
                {lang === 'tr' ? 'Not: Mikrofon mevcut sayfanın ilk ayetiyle karşılaştırılır.' : 'Note: mic compares to the first ayah on this page.'}
              </p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes pageFlipFwd {
          0%   { transform: rotateY(0deg);   opacity: 1; }
          50%  { transform: rotateY(-12deg) translateX(-6px); opacity: 0.85; }
          100% { transform: rotateY(0deg);   opacity: 1; }
        }
        @keyframes pageFlipBwd {
          0%   { transform: rotateY(0deg);   opacity: 1; }
          50%  { transform: rotateY(12deg) translateX(6px); opacity: 0.85; }
          100% { transform: rotateY(0deg);   opacity: 1; }
        }
      `}</style>
    </AppShell>
  );
};

export default HafizlikReader;
